import { EnvironmentProviders, makeEnvironmentProviders, inject, provideAppInitializer } from '@angular/core';
import {
    BrowserCacheLocation,
    InteractionType,
    IPublicClientApplication,
    LogLevel,
    PublicClientApplication
} from '@azure/msal-browser';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastConfiguration,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService
} from '@azure/msal-angular';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { from } from 'rxjs';
import { environment } from '../environments/environment';

export class MsalAuth {
    private msalInstance: IPublicClientApplication;

    constructor() {
        this.msalInstance = new PublicClientApplication({
            auth: {
                clientId: environment.entraIdentity.clientId,
                authority: environment.entraIdentity.authority,
                redirectUri: environment.siteUrl,
                postLogoutRedirectUri: environment.siteUrl,
                navigateToLoginRequestUrl: true
            },
            cache: {
                cacheLocation: BrowserCacheLocation.LocalStorage,
                storeAuthStateInCookie: this.isIE(),
            },
            system: {
                loggerOptions: {
                    loggerCallback: this.loggerCallback,
                    logLevel: LogLevel.Trace,
                    piiLoggingEnabled: false,
                },
            },
        });
    }

    private isIE(): boolean {
        return window.navigator.userAgent.indexOf('MSIE ') > -1 ||
            window.navigator.userAgent.indexOf('Trident/') > -1;
    }

    private loggerCallback(logLevel: LogLevel, message: string) {
        if(logLevel > LogLevel.Trace) {
            console.log(message);
        }
    }

    async init() {
        await this.msalInstance.initialize();
        return from(this.msalInstance.handleRedirectPromise().then(() => {
            // Check if there's a user signed in
            const accounts = this.msalInstance.getAllAccounts();
            if (accounts.length === 0) {
                // No user signed in, trigger login
                return this.msalInstance.loginRedirect({
                    scopes: ['openid', 'profile', 'user.read']
                });
            }

            return Promise.resolve();
        }));
    }

    getInstance(): IPublicClientApplication {
        return this.msalInstance;
    }

    getInterceptorConfig(): MsalInterceptorConfiguration {
        const protectedResourceMap = new Map<string, Array<string>>();
        protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);

        return {
            interactionType: InteractionType.Redirect,
            protectedResourceMap
        };
    }

    getGuardConfig(): MsalGuardConfiguration {
        return {
            interactionType: InteractionType.Redirect,
            authRequest: {
                scopes: ['openid', 'profile', 'user.read']
            }
        };
    }
}

export function provideMsal(): EnvironmentProviders {
    return makeEnvironmentProviders([
        MsalAuth,
        Location,
        Router,
        {
            provide: MSAL_INSTANCE,
            useFactory: (msalAuth: MsalAuth) => msalAuth.getInstance(),
            deps: [MsalAuth]
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: (msalAuth: MsalAuth) => msalAuth.getInterceptorConfig(),
            deps: [MsalAuth]
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: (msalAuth: MsalAuth) => msalAuth.getGuardConfig(),
            deps: [MsalAuth]
        },
        {
            provide: MsalService,
            useFactory: (
                instance: IPublicClientApplication,
                location: Location
            ) => new MsalService(instance, location),
            deps: [MSAL_INSTANCE, Location]
        },
        {
            provide: MsalGuard,
            useFactory: (
                msalGuardConfig: MsalGuardConfiguration,
                msalBroadcastService: MsalBroadcastService,
                msalService: MsalService,
                location: Location,
                router: Router
            ) => new MsalGuard(msalGuardConfig, msalBroadcastService, msalService, location, router),
            deps: [MSAL_GUARD_CONFIG, MsalBroadcastService, MsalService, Location, Router]
        },
        {
            provide: MsalBroadcastService,
            useFactory: (
                instance: IPublicClientApplication,
                auth: MsalService,
                msalBroadcastConfig: MsalBroadcastConfiguration
            ) => new MsalBroadcastService(instance, auth, msalBroadcastConfig),
            deps: [MSAL_INSTANCE, MsalService, MSAL_GUARD_CONFIG]
        },
        {
            provide: MsalInterceptor,
            useFactory: (
                msalInterceptorConfig: MsalInterceptorConfiguration,
                authService: MsalService,
                location: Location,
                msalBroadcastService: MsalBroadcastService
            ) => new MsalInterceptor(msalInterceptorConfig, authService, location, msalBroadcastService),
            deps: [MSAL_INTERCEPTOR_CONFIG, MsalService, Location, MsalBroadcastService]
        },
        provideAppInitializer(() => {
        const initializerFn = ((msalAuth: MsalAuth) => () => msalAuth.init())(inject(MsalAuth));
        return initializerFn();
      })
    ]);
}
